// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrganizationUser } from '@/account/api/types';
import { formatPersonName } from '@/account/utilities/format-person-name';
import { Box, Link, Stack, Typography } from '@mui/material';

interface Props {
  /**
   * The member registered as the contract contact, when one has been recorded
   */
  contractContact: OrganizationUser | null;

  /**
   * The member registered as the billing contact, when one has been recorded
   */
  billingContact: OrganizationUser | null;

  /**
   * The surface the contacts sit on. Email links keep the theme's link color
   * on a light surface and inherit the text color on a dark tooltip, where the
   * primary color would not have enough contrast.
   */
  surface: 'light' | 'dark';
}

interface ContactCardProps {
  /**
   * The role the contact is registered for, such as "Contract"
   */
  label: string;

  /**
   * The registered member, or null when nobody is registered for the role
   */
  contact: OrganizationUser | null;

  /**
   * The surface the card sits on, see `Props.surface`
   */
  surface: 'light' | 'dark';
}

/**
 * One registered contact, centered under its role: the name, then the email.
 * Secondary text is dimmed with opacity rather than a palette token so it
 * reads on both a light dialog and a dark tooltip.
 */
function ContactCard(props: ContactCardProps) {
  return (
    <Stack alignItems="center" spacing={0.25} textAlign="center">
      <Typography
        variant="caption"
        fontWeight="bold"
        textTransform="uppercase"
        sx={{ opacity: 0.7 }}
      >
        {props.label}
      </Typography>
      {props.contact === null ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Not registered
        </Typography>
      ) : (
        <>
          <Typography variant="body2" fontWeight="medium">
            {formatPersonName(props.contact)}
          </Typography>
          {props.contact.email !== null && (
            <Link
              href={`mailto:${props.contact.email}`}
              variant="caption"
              color={props.surface === 'dark' ? 'inherit' : 'primary'}
              underline="always"
            >
              {props.contact.email}
            </Link>
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * The contacts Hardfin has registered for the organization, shown wherever the
 * subscription expiration notice appears so a user knows who to reach. The two
 * roles sit side by side under one centered sentence.
 */
export function RegisteredContacts(props: Props) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" textAlign="center">
        Hardfin has the following contacts registered for your organization:
      </Typography>
      <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={2}>
        <ContactCard
          label="Contract"
          contact={props.contractContact}
          surface={props.surface}
        />
        <ContactCard
          label="Billing"
          contact={props.billingContact}
          surface={props.surface}
        />
      </Box>
    </Stack>
  );
}
